import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  MESSAGE_TEMPLATE_STATUS_LABEL,
  type MessageTemplateRecord,
  type MessageTemplateStatus,
} from '@workspace/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useConfirm } from '@/contexts/confirmContext';
import { toast } from 'react-toastify';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from './ContactAgentPageHeader';
import { ContactAgentTenantPicker } from './ContactAgentTenantPicker';
import { MessageTemplateEditor } from './MessageTemplateEditor';
import { emptyTemplateDraft, type MessageTemplateDraft } from './MessageTemplateEditor.utils';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';

function draftFromRecord(row: MessageTemplateRecord): MessageTemplateDraft {
  return {
    channelKind: row.channelKind === 'EMAIL' ? 'EMAIL' : 'SMS',
    name: row.name,
    subject: row.subject ?? '',
    bodyText: row.bodyText,
    bodyHtml: row.bodyHtml ?? '',
  };
}

function statusVariant(status: MessageTemplateStatus): 'default' | 'secondary' | 'outline' {
  if (status === 'ACTIVE') {
    return 'default';
  }
  if (status === 'ARCHIVED') {
    return 'outline';
  }
  return 'secondary';
}

export function CampaignTemplatesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const { confirm } = useConfirm();
  const kindParam = searchParams.get('kind') === 'EMAIL' ? 'EMAIL' : 'SMS';
  const returnTo = (searchParams.get('returnTo') ?? '').slice(0, 64);
  const [kind, setKind] = useState<'SMS' | 'EMAIL'>(kindParam);
  const [items, setItems] = useState<MessageTemplateRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | 'new' | null>(returnTo === 'wizard' ? 'new' : null);
  const [draft, setDraft] = useState<MessageTemplateDraft>(emptyTemplateDraft(kindParam));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selected = items.find(item => item.id === selectedId) ?? null;
  const archived = selected?.status === 'ARCHIVED';

  const reload = async (keepId?: string) => {
    const list = await contactAgentApi.listMessageTemplates({ channelKind: kind });
    setItems(list);
    if (keepId && list.some(item => item.id === keepId)) {
      setSelectedId(keepId);
      const row = list.find(item => item.id === keepId);
      if (row) {
        setDraft(draftFromRecord(row));
      }
    }
  };

  useEffect(() => {
    if (!ready) {
      return;
    }
    setError('');
    void contactAgentApi
      .listMessageTemplates({ channelKind: kind })
      .then(setItems)
      .catch(err => setError(contactAgentError(err)));
  }, [ready, tenantId, kind]);

  const startNew = () => {
    setSelectedId('new');
    setDraft(emptyTemplateDraft(kind));
  };

  const selectRow = (row: MessageTemplateRecord) => {
    setSelectedId(row.id);
    setDraft(draftFromRecord(row));
  };

  const save = async (activate: boolean) => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: draft.name.trim(),
        subject: draft.channelKind === 'EMAIL' ? draft.subject.trim() : undefined,
        bodyText: draft.bodyText,
        bodyHtml: draft.channelKind === 'EMAIL' ? draft.bodyHtml.trim() || undefined : undefined,
      };
      let saved: MessageTemplateRecord;
      if (selectedId && selectedId !== 'new') {
        saved = await contactAgentApi.updateMessageTemplate(selectedId, {
          ...payload,
          status: activate ? 'ACTIVE' : undefined,
        });
      } else {
        saved = await contactAgentApi.createMessageTemplate({
          channelKind: draft.channelKind,
          ...payload,
        });
        if (activate) {
          saved = await contactAgentApi.updateMessageTemplate(saved.id, { status: 'ACTIVE' });
        }
      }
      toast.success(activate ? 'Plantilla activa' : 'Plantilla guardada');
      await reload(saved.id);
      if (returnTo === 'wizard' && saved.status === 'ACTIVE') {
        navigate(
          `/admin/campaigns/new?messageTemplateId=${saved.id}&channelKind=${saved.channelKind}&deliveryMode=NOTIFICATION`,
        );
      }
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    if (!selected) {
      return;
    }
    setSaving(true);
    try {
      const copy = await contactAgentApi.duplicateMessageTemplate(selected.id);
      toast.success('Copia creada como borrador');
      await reload(copy.id);
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!selected) {
      return;
    }
    const ok = await confirm({
      title: 'Archivar plantilla',
      message: 'Las campañas que ya la usan no se tocan. No vas a poder elegirla en campañas nuevas.',
      confirmText: 'Archivar',
    });
    if (!ok) {
      return;
    }
    setSaving(true);
    try {
      await contactAgentApi.archiveMessageTemplate(selected.id);
      toast.success('Plantilla archivada');
      await reload(selected.id);
    } catch (err) {
      setError(contactAgentError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        eyebrow='Campañas'
        title='Plantillas SMS y email'
        description='Reutilizalas en campañas de notificacion. WhatsApp sigue usando las plantillas aprobadas del canal.'
        backTo='/admin/campaigns'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
        actions={
          <Button type='button' onClick={startNew}>
            Nueva plantilla
          </Button>
        }
      />
      <div className='flex flex-wrap gap-2'>
        {(['SMS', 'EMAIL'] as const).map(option => (
          <Button
            key={option}
            type='button'
            size='sm'
            variant={kind === option ? 'default' : 'outline'}
            onClick={() => {
              setKind(option);
              setSelectedId(null);
              setSearchParams(current => {
                current.set('kind', option);
                return current;
              });
            }}
          >
            {option === 'SMS' ? 'SMS' : 'Email'}
          </Button>
        ))}
      </div>
      {error ? <p className='text-sm text-destructive'>{error}</p> : null}
      <div className='grid gap-6 lg:grid-cols-[280px_1fr]'>
        <Card className='rounded-2xl shadow-sm'>
          <CardHeader>
            <CardTitle className='text-base'>Biblioteca</CardTitle>
          </CardHeader>
          <CardContent className='space-y-1'>
            {items.length === 0 ? (
              <p className='text-sm text-muted-foreground'>Todavia no hay plantillas de {kind === 'SMS' ? 'SMS' : 'email'}.</p>
            ) : (
              items.map(item => (
                <button
                  key={item.id}
                  type='button'
                  onClick={() => selectRow(item)}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${
                    selectedId === item.id ? 'bg-muted font-medium' : 'hover:bg-muted/60'
                  }`}
                >
                  <span className='truncate'>{item.name}</span>
                  <Badge variant={statusVariant(item.status)}>{MESSAGE_TEMPLATE_STATUS_LABEL[item.status]}</Badge>
                </button>
              ))
            )}
          </CardContent>
        </Card>
        <Card className='rounded-2xl shadow-sm'>
          <CardHeader>
            <CardTitle className='text-base'>
              {selectedId === 'new' ? 'Nueva plantilla' : selected?.name ?? 'Elegi una plantilla'}
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            {selectedId ? (
              archived && selected ? (
                <div className='space-y-4'>
                  <p className='text-sm text-muted-foreground'>Archivada. Duplicarla para volver a usarla.</p>
                  <Button type='button' variant='outline' disabled={saving} onClick={() => void duplicate()}>
                    Duplicar
                  </Button>
                </div>
              ) : (
                <>
                  <MessageTemplateEditor value={draft} onChange={setDraft} kindLocked={selectedId !== 'new'} />
                  <div className='flex flex-wrap gap-2'>
                    <Button type='button' disabled={saving} onClick={() => void save(false)}>
                      {saving ? 'Guardando...' : 'Guardar borrador'}
                    </Button>
                    <Button type='button' variant='outline' disabled={saving} onClick={() => void save(true)}>
                      {returnTo === 'wizard' ? 'Activar y usar' : 'Activar'}
                    </Button>
                    {selected ? (
                      <>
                        <Button type='button' variant='outline' disabled={saving} onClick={() => void duplicate()}>
                          Duplicar
                        </Button>
                        <Button type='button' variant='ghost' disabled={saving} onClick={() => void archive()}>
                          Archivar
                        </Button>
                      </>
                    ) : null}
                  </div>
                </>
              )
            ) : (
              <p className='text-sm text-muted-foreground'>
                Elegi una de la lista o crea una nueva. Las campanas de notificacion solo eligen plantillas activas.
              </p>
            )}
            {returnTo === 'wizard' ? (
              <p className='text-xs text-muted-foreground'>
                Cuando la actives volves al asistente de campana.{' '}
                <Link className='underline' to='/admin/campaigns/new'>
                  Volver sin elegir
                </Link>
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </ContactAgentPage>
  );
}
